import type { Post, NewPost } from '@server/db/schema'
import type { PublicUser } from '@server/auth/session'
import type { SeoConfig } from '@shared/seo.config'

// ─────────────────────────────────────────────────────────────────────────────
// THE CLIENT ↔ SERVER BRIDGE. This is the biggest payoff of keeping both halves
// in one codebase: the `Post` below is THE type generated from
// server/db/schema.ts. Change a column in the schema and TypeScript fails right
// at the component that uses it — client and server can no longer drift apart
// and find out at runtime.
//
// Components DO NOT fetch directly. Add an endpoint in server/routes/index.ts,
// then add the matching function here.
// ─────────────────────────────────────────────────────────────────────────────

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    /**
     * Stable machine-readable reason, e.g. 'auth.invalid_credentials'. The
     * server sends it alongside its English `message` (server/router.ts), and
     * `translateError` in src/i18n/ turns it into the visitor's language.
     *
     * This is why error text can be multilingual at all: translating the
     * server's sentence would mean matching on English prose, which breaks the
     * moment someone rewords a message.
     */
    readonly code?: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    // REQUIRED. The session lives in an HttpOnly cookie; without this line
    // fetch sends no cookie at all and every authenticated route answers 401,
    // even immediately after a successful sign-in.
    credentials: 'include',
    headers: { 'content-type': 'application/json', ...(init?.headers ?? {}) },
  })
  const text = await res.text()
  const payload = text ? JSON.parse(text) : null
  if (!res.ok) {
    throw new ApiError(payload?.error ?? `HTTP ${res.status}`, res.status, payload?.code)
  }
  return payload as T
}

export const api = {
  health: () => request<{ ok: boolean; database: 'configured' | 'missing' }>('/api/health'),

  auth: {
    me: () => request<{ user: PublicUser | null }>('/api/auth/me'),
    register: (email: string, password: string, name?: string) =>
      request<{ user: PublicUser }>('/api/auth/register', {
        method: 'POST',
        body: JSON.stringify({ email, password, name }),
      }),
    login: (email: string, password: string) =>
      request<{ user: PublicUser }>('/api/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      }),
    google: (credential?: string, profile?: { email?: string; name?: string; avatar?: string; googleId?: string }) =>
      request<{ user: PublicUser }>('/api/auth/google', {
        method: 'POST',
        body: JSON.stringify({ credential, ...profile }),
      }),
    logout: () => request<{ ok: true }>('/api/auth/logout', { method: 'POST' }),
    updateProfile: (name: string) =>
      request<{ user: PublicUser }>('/api/auth/me', { method: 'PATCH', body: JSON.stringify({ name }) }),
    changePassword: (current: string, next: string) =>
      request<{ ok: true }>('/api/auth/password', { method: 'POST', body: JSON.stringify({ current, next }) }),
  },

  seo: {
    get: () => request<SeoConfig>('/api/settings/seo'),
    save: (patch: Partial<SeoConfig>) =>
      request<SeoConfig>('/api/settings/seo', { method: 'PUT', body: JSON.stringify(patch) }),
  },

  posts: {
    list: () => request<Post[]>('/api/posts'),
    get: (id: string) => request<Post>(`/api/posts/${id}`),
    create: (input: Pick<NewPost, 'title' | 'body'>) =>
      request<Post>('/api/posts', { method: 'POST', body: JSON.stringify(input) }),
    update: (id: string, input: Partial<Pick<NewPost, 'title' | 'body' | 'published'>>) =>
      request<Post>(`/api/posts/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
    remove: (id: string) =>
      request<{ ok: true }>(`/api/posts/${id}`, { method: 'DELETE' }),
  },
}

export type { Post, NewPost, SeoConfig, PublicUser }
